import { useEffect, useRef, useState } from "react";
import type { HelpDocsConfig } from "./useHelpDocsLighthouseTypes";

export const useHelpDocsLighthouse = (config: HelpDocsConfig) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // track whether we've ever initialized
  const initialized = useRef(false);
  // remember last values so we only reload when they change
  const lastKey = useRef<string>();
  const lastLang = useRef<string>();

  useEffect(() => {
    // don't run on server
    if (typeof window === "undefined") {
      return;
    }

    // should we tear down & re-init?
    const shouldReload =
      config.widget_key !== lastKey.current || config.language_code !== lastLang.current;

    if (!shouldReload && initialized.current) {
      return;
    }

    // mark new values
    lastKey.current = config.widget_key;
    lastLang.current = config.language_code;
    initialized.current = true;
    setIsLoaded(false);

    // expose the new config (with our onLoad wrapper)
    window.hdlh = {
      ...config,
      onLoad: () => {
        console.debug("✅ HelpDocs Lighthouse loaded.");
        setIsLoaded(true);
        config.onLoad?.();
      },
    };

    // inject the script _after_ setting window.hdlh
    const script = document.createElement("script");
    script.src = `https://lighthouse.helpdocs.io/load?t=${Date.now()}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      console.error("❌ Failed to load HelpDocs Lighthouse script.");
    };
    document.head.appendChild(script);

    // no manual cleanup of script or window.hdlh —
    // Lighthouse will manage its own lifecycle,
    // and multiple injections simply update it on each change.
  }, [config.widget_key, config.language_code]);

  const openWidget = () => {
    if (isLoaded && window.Lighthouse?.show) {
      window.Lighthouse.show();
    } else {
      console.warn("⚠️ HelpDocs Lighthouse is not ready yet.");
    }
  };

  return { openWidget, isLoaded };
};
